import { useEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import {
  HEAR_ABOUT_US_COLOR_BY_SOURCE,
  HearAboutUsSource,
} from "../../constants/hearAboutUs";
import {
  HearAboutUsOtherDetail,
  HearAboutUsSourceStat,
} from "../../schemas/user";

interface HearAboutUsPieChartProps {
  sources: HearAboutUsSourceStat[];
  otherDetails: HearAboutUsOtherDetail[];
  total: number;
}

const PIE_DEPTH = 28;
const MIN_LABEL_PERCENT = 6;

const formatPercent = (percent: number): string => percent.toFixed(1);

const getSourceColor = (source: string): string =>
  HEAR_ABOUT_US_COLOR_BY_SOURCE[source as HearAboutUsSource] || "#90a4ae";

const darkenHex = (hex: string, amount: number): string => {
  const normalized = hex.replace("#", "");
  const num = parseInt(normalized, 16);
  if (Number.isNaN(num)) {
    return hex;
  }
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)));
  return `rgb(${r}, ${g}, ${b})`;
};

const draw3DPie = (
  canvas: HTMLCanvasElement,
  slices: HearAboutUsSourceStat[]
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (width === 0 || height === 0) {
    return;
  }

  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const visibleSlices = slices.filter((slice) => slice.percent > 0);
  if (visibleSlices.length === 0) {
    return;
  }

  const layers = Math.max(12, Math.min(PIE_DEPTH, Math.round(height * 0.09)));
  const rx = Math.min(width * 0.46, (height - layers - 18) / 1.05);
  const ry = rx * 0.42;
  const cx = width / 2;
  const cy = (height - layers) / 2;
  const labelSize = Math.max(13, Math.min(22, rx * 0.12));

  ctx.save();
  ctx.fillStyle = "rgba(15, 23, 42, 0.16)";
  ctx.beginPath();
  ctx.ellipse(cx, cy + layers + 14, rx * 0.9, ry * 0.55, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  let angle = -Math.PI / 2;
  const pieSlices = visibleSlices.map((slice) => {
    const start = angle;
    const sweep = (slice.percent / 100) * Math.PI * 2;
    const end = start + sweep;
    angle = end;
    return { ...slice, start, end, color: getSourceColor(slice.source) };
  });

  for (let layer = layers; layer >= 0; layer -= 1) {
    const offsetY = cy + layer;
    const isTop = layer === 0;
    pieSlices.forEach((slice) => {
      ctx.beginPath();
      ctx.moveTo(cx, offsetY);
      ctx.ellipse(cx, offsetY, rx, ry, 0, slice.start, slice.end, false);
      ctx.closePath();
      ctx.fillStyle = isTop ? slice.color : darkenHex(slice.color, 0.32);
      ctx.fill();
      if (isTop) {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
        ctx.lineWidth = 1.25;
        ctx.stroke();
      }
    });
  }

  pieSlices.forEach((slice) => {
    if (slice.percent < MIN_LABEL_PERCENT) {
      return;
    }
    const mid = (slice.start + slice.end) / 2;
    const x = cx + Math.cos(mid) * rx * 0.52;
    const y = cy + Math.sin(mid) * ry * 0.52;
    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${labelSize}px Poppins, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
    ctx.shadowBlur = 5;
    ctx.fillText(`${formatPercent(slice.percent)}%`, x, y);
    ctx.restore();
  });
};

const HearAboutUsPieChart = ({
  sources,
  otherDetails,
  total,
}: HearAboutUsPieChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return;
    }

    const render = () => draw3DPie(canvas, sources);
    render();

    const observer = new ResizeObserver(() => render());
    observer.observe(container);
    return () => observer.disconnect();
  }, [sources]);

  if (total === 0) {
    return (
      <Box
        sx={{
          minHeight: { xs: 180, sm: 280 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
          px: 1,
          textAlign: "center",
        }}
      >
        <Typography>No signup feedback yet</Typography>
      </Box>
    );
  }

  return (
     <Grid container spacing={{ xs: 2, md: 3 }} alignItems={{ xs: "stretch", md: "center" }}>
      <Grid item xs={12} md={7}>
        <Box
          ref={containerRef}
          sx={{
            width: "100%",
            height: { xs: 240, sm: 320, md: 380 },
          }}
        >
          <canvas
            ref={canvasRef}
            style={{ width: "100%", height: "100%", display: "block" }}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={5}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 1.5 } }}>
          <Typography variant="subtitle2" color="text.secondary">
            {total} {total === 1 ? "response" : "responses"}
          </Typography>
          {sources.map((source) => (
            <Box
              key={source.source}
              sx={{
                py: { xs: 0.75, sm: 0 },
                borderBottom: { xs: "1px solid", md: "none" },
                borderColor: { xs: "divider", md: "transparent" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: getSourceColor(source.source),
                    }}
                  />
                  <Typography sx={{ fontWeight: 600 }} noWrap>
                    {source.label}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {source.count} ({formatPercent(source.percent)}%)
                </Typography>
              </Box>
              {source.source === "others" && otherDetails.length > 0 && (
                <Box sx={{ pl: 3.5, pt: 0.5 }}>
                  {otherDetails.map((detail) => (
                    <Typography
                      key={detail.text}
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {detail.text} — {detail.count}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Grid>
    </Grid>
  );
};

export default HearAboutUsPieChart;
